<script setup>
import { ref, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import BankSettingsAPI from 'dashboard/api/bankSettings';

const loading = ref(false);
const settings = ref([]);
const activeBank = ref(null);
const editingCreds = ref({});

const BANK_LABELS = {
  uy3: 'UY3',
  facta: 'FACTA',
  v8: 'V8',
  c6: 'C6',
};

const CREDENTIAL_FIELDS = {
  uy3: [
    { key: 'auth_url', label: 'URL de Autenticação', placeholder: 'https://backoffice-seguefinanceira.auth.sa-east-1.amazoncognito.com' },
    { key: 'api_url', label: 'URL da API', placeholder: 'https://api.uy3.com.br' },
    { key: 'basic_token', label: 'Basic Token', secret: true },
    { key: 'scope', label: 'Scope', placeholder: 'credit/SegueFinanceiraAPI' },
  ],
  facta: [
    { key: 'base_url', label: 'URL Base', placeholder: 'https://webservice.facta.com.br' },
    { key: 'credentials_base64', label: 'Credenciais Base64', secret: true },
    { key: 'login_certificado', label: 'Login Certificado', placeholder: '94039' },
  ],
  v8: [
    { key: 'auth_url', label: 'URL de Autenticação' },
    { key: 'api_url', label: 'URL da API' },
    { key: 'username', label: 'Usuário' },
    { key: 'password', label: 'Senha', secret: true },
    { key: 'client_id', label: 'Client ID', secret: true },
  ],
  c6: [
    { key: 'api_base', label: 'URL Base' },
    { key: 'username', label: 'Usuário' },
    { key: 'password', label: 'Senha', secret: true },
  ],
};

const fetchSettings = async () => {
  loading.value = true;
  try {
    const response = await BankSettingsAPI.get();
    settings.value = response.data.data || [];
  } catch (e) {
    useAlert('Erro ao carregar configurações de bancos');
  } finally {
    loading.value = false;
  }
};

const openBank = async bank => {
  activeBank.value = bank;
  const existing = settings.value.find(s => s.bank_name === bank);
  if (existing) {
    try {
      const response = await BankSettingsAPI.show(existing.id);
      editingCreds.value = { ...(response.data.credentials || {}) };
    } catch (e) {
      editingCreds.value = {};
    }
  } else {
    editingCreds.value = {};
  }
};

const saveCredentials = async () => {
  const existing = settings.value.find(s => s.bank_name === activeBank.value);
  const payload = {
    bank_setting: {
      bank_name: activeBank.value,
      enabled: true,
      credentials: editingCreds.value,
    },
  };
  try {
    if (existing) {
      await BankSettingsAPI.update(existing.id, payload);
    } else {
      await BankSettingsAPI.create(payload);
    }
    useAlert('Credenciais salvas com sucesso!');
    activeBank.value = null;
    editingCreds.value = {};
    await fetchSettings();
  } catch (e) {
    useAlert('Erro ao salvar credenciais');
  }
};

const getStatus = bank => {
  const existing = settings.value.find(s => s.bank_name === bank);
  if (!existing) return { label: 'Não configurado', color: 'text-slate-500' };
  if (!existing.enabled) return { label: 'Desabilitado', color: 'text-yellow-600' };
  if (existing.token_expired) return { label: 'Token expirado (renova no próximo uso)', color: 'text-blue-600' };
  return { label: 'Configurado ✓', color: 'text-green-600' };
};

onMounted(fetchSettings);
</script>

<template>
  <div class="p-6 max-w-4xl">
    <h1 class="text-2xl font-semibold mb-2">Credenciais dos Bancos</h1>
    <p class="text-sm text-slate-600 mb-6">
      Configure as credenciais de cada banco. Apenas administradores podem editar.
      Os tokens de acesso são renovados automaticamente quando expiram.
    </p>

    <div v-if="loading" class="text-center py-8">Carregando...</div>

    <div v-else class="grid grid-cols-2 gap-4">
      <button
        v-for="bank in ['uy3', 'facta', 'v8', 'c6']"
        :key="bank"
        type="button"
        class="text-left border-2 border-slate-300 rounded-lg p-4 bg-white hover:border-green-500 hover:bg-green-50 cursor-pointer transition"
        @click="openBank(bank)"
      >
        <div class="flex justify-between items-start mb-2">
          <h3 class="text-lg font-bold">{{ BANK_LABELS[bank] }}</h3>
          <span class="text-sm text-blue-600 font-medium">
            {{ settings.find(s => s.bank_name === bank) ? 'Editar' : 'Configurar' }}
          </span>
        </div>
        <div :class="['text-xs', getStatus(bank).color]">
          {{ getStatus(bank).label }}
        </div>
      </button>
    </div>

    <!-- Modal de edição -->
    <div
      v-if="activeBank"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="activeBank = null"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
        <h2 class="text-xl font-semibold mb-4">Credenciais {{ BANK_LABELS[activeBank] }}</h2>

        <div class="space-y-3">
          <label
            v-for="field in CREDENTIAL_FIELDS[activeBank]"
            :key="field.key"
            class="block"
          >
            <span class="text-sm font-medium">{{ field.label }}</span>
            <input
              v-model="editingCreds[field.key]"
              :type="field.secret ? 'password' : 'text'"
              :placeholder="field.placeholder || ''"
              class="mt-1 block w-full rounded border-slate-300 px-3 py-2"
            >
          </label>
        </div>

        <div class="flex gap-2 mt-6">
          <button
            class="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
            @click="saveCredentials"
          >
            Salvar
          </button>
          <button
            class="flex-1 border border-slate-300 py-2 rounded hover:bg-slate-50"
            @click="activeBank = null"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
